"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setStorageInstance = exports.newBlockchain = void 0;
const immutable_1 = require("immutable");
const compiler_1 = require("./compiler");
const mem_reader_1 = require("./mem-reader");
const storage_1 = require("./storage");
const uint256_1 = require("./uint256");
const utils_1 = require("./utils");
const newStore = (0, immutable_1.Record)({
    session: null,
    currentTx: null,
    storages: (0, immutable_1.Map)(),
    callStack: (0, immutable_1.List)(),
    contracts: (0, immutable_1.Map)(),
});
function newBlockchain(session) {
    return new BlockchainState(newStore().set('session', session));
}
exports.newBlockchain = newBlockchain;
class BlockchainState {
    constructor(store) {
        this.store = store;
    }
    get stack() {
        const last = this.store.callStack.last();
        if (!last) {
            throw new Error('No running transaction');
        }
        return last;
    }
    get timestamp() {
        return this.store.currentTx.timestamp;
    }
    get difficulty() {
        return this.store.currentTx.difficulty;
    }
    get gasLimit() {
        return this.stack.gasLimit;
    }
    get callvalue() {
        return this.stack.callValue;
    }
    get calldata() {
        return this.stack.calldata;
    }
    get static() {
        return this.stack.static;
    }
    get session() {
        return this.store.session;
    }
    get gas() {
        return this.stack.gas.copy();
    }
    get gasSpent() {
        return this.stack.gasLimit.sub(this.stack.gas);
    }
    get gasPrice() {
        return this.shared.gasPrice.copy();
    }
    get caller() {
        return this.stack.caller;
    }
    get shared() {
        return this.store.currentTx;
    }
    get address() {
        return this.stack.address;
    }
    get origin() {
        return this.store.currentTx.origin;
    }
    get current0x() {
        return (0, utils_1.to0xAddress)(this.stack.currentStorageCtx);
    }
    get currentStorage() {
        return this.getStorageOf(this.current0x);
    }
    async getContract(hex) {
        hex = typeof hex === 'string' ? hex : (0, utils_1.to0xAddress)(hex);
        const contractAddress = (0, utils_1.toAddress)(hex);
        let compiled = this.store.contracts.get(hex);
        if (!compiled) {
            const code = await this.getBytecodeFromCache(hex);
            compiled = (0, compiler_1.compileCode)(code, this.session.opts?.contractsNames?.[hex], contractAddress, undefined, this.session.opts?.cacheDir);
            const contracts = this.store.contracts.set(hex, compiled);
            this.store = this.store.set('contracts', contracts);
        }
        return compiled;
    }
    async getBytecodeFromCache(contract) {
        const { readCache, writeCache } = (0, utils_1.getNodejsLibs)(this.session.opts?.cacheDir);
        const cacheFile = `bytecode/${contract}.bytecode`;
        if (readCache) {
            const cached = readCache(cacheFile);
            if (cached) {
                return Buffer.from(cached, 'hex').subarray();
            }
        }
        const online = await this.session.rpc.getCode(contract);
        if (writeCache) {
            writeCache(cacheFile, Buffer.from(online).toString('hex'));
        }
        return online;
    }
    setContract(contract) {
        const contracts = this.store.contracts.set((0, utils_1.to0xAddress)(contract.contractAddress), contract);
        return new BlockchainState(this.store.set('contracts', contracts));
    }
    getStorageOf(hex) {
        hex = typeof hex === 'string' ? hex : (0, utils_1.to0xAddress)(hex);
        let cached = this.store.storages.get(hex);
        if (!cached) {
            const storages = this.store.storages.set(hex, cached = new storage_1.RpcStorage(hex, this.session.rpc));
            this.store = this.store.set('storages', storages);
        }
        return cached;
    }
    _changeStorage(hex, fn) {
        const currentStorage = this.getStorageOf(hex);
        const newStorage = fn(currentStorage);
        return new BlockchainState(this.store.set('storages', this.store.storages.set(hex, newStorage)));
    }
    getStorage(location) {
        return this.currentStorage.get(location);
    }
    getBalance() {
        return this.currentStorage.getBalance();
    }
    setStorage(location, value) {
        return this._changeStorage(this.current0x, s => s.set(location, value));
    }
    async transferFrom(_from, _to, value) {
        if (value.eq(0)) {
            return this;
        }
        const from = (0, utils_1.to0xAddress)(_from);
        const to = (0, utils_1.to0xAddress)(_to);
        const fromBalance = await this.getStorageOf(from).getBalance();
        if (fromBalance.lt(value)) {
            throw new Error(`Insufficient balance in ${from} to transfer ${(0, utils_1.dumpU256)(value)} to ${to}`);
        }
        return this._changeStorage(from, s => s.decrementBalance(value))
            ._changeStorage(to, s => s.incrementBalance(value));
    }
    transfer(_to, value) {
        return this.transferFrom(this.stack.currentStorageCtx, _to, value);
    }
    mintValue(toAddress, value) {
        return this._changeStorage((0, utils_1.to0xAddress)(toAddress), s => s.incrementBalance(value));
    }
    decrementGas(num) {
    }
    async newTx(data) {
        if (this.store.callStack.size) {
            throw new Error('A tx is already being executed');
        }
        const full = {
            address: data.address ?? data.contract,
            calldata: new mem_reader_1.MemReader(data.calldata),
            caller: data.caller ?? data.origin,
            callValue: data.callvalue,
            currentStorageCtx: data.contract,
            gas: data.gasLimit.copy(),
            gasLimit: data.gasLimit.copy(),
            retdatasize: data.retdatasize,
            static: data.static,
        };
        let ret = new BlockchainState(this.store
            .set('currentTx', {
            timestamp: data.timestamp,
            gasPrice: data.gasPrice,
            origin: data.origin,
            difficulty: data.difficulty ?? (0, uint256_1.U256)(0),
        }));
        ret = await ret.transferFrom(full.caller, full.address, full.callValue);
        return ret.pushCallStack(full);
    }
    pushCallStack(stack) {
        return new BlockchainState(this.store.set('callStack', this.store.callStack.push({
            ...this.store.callStack.last(),
            ...stack,
        })));
    }
    setStorageLocation(address, storage) {
        throw new Error('Method not implemented.');
    }
    async pushCallTo(contract, callValue, calldata, retdatasize, gasLimit) {
        if (this.static) {
            throw new Error('Opcode "call" is not valid on a static execution context');
        }
        let ret = await this
            .transfer(contract, callValue);
        return ret.pushCallStack({
            ...this._buildStack(calldata, retdatasize, gasLimit),
            address: contract,
            currentStorageCtx: contract,
            callValue,
            caller: this.address,
        });
    }
    pushDelegatecallTo(contract, calldata, retdatasize, gasLimit) {
        return this.pushCallStack({
            ...this._buildStack(calldata, retdatasize, gasLimit),
        });
    }
    pushStaticcallTo(contract, calldata, retdatasize, gasLimit) {
        return this.pushCallStack({
            ...this._buildStack(calldata, retdatasize, gasLimit),
            address: contract,
            currentStorageCtx: contract,
            caller: this.address,
            callValue: (0, uint256_1.U256)(0),
            static: true,
        });
    }
    _buildStack(calldata, retdatasize, gasLimit) {
        return {
            calldata: new mem_reader_1.MemReader(calldata),
            retdatasize,
            gasLimit: gasLimit.copy(),
            gas: gasLimit.copy(),
        };
    }
    popCallStack() {
        const ret = new BlockchainState(this.store.set('callStack', this.store.callStack.pop()));
        return ret;
    }
    setStorageInstance(contract, storage) {
        this.store = this.store.set('storages', this.store.storages.set((0, utils_1.to0xAddress)(contract), storage));
    }
}
function setStorageInstance(inState, contract, storage) {
    return inState.setStorageInstance(contract, storage);
}
exports.setStorageInstance = setStorageInstance;
//# sourceMappingURL=blockchain-state.js.map